import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, switchMap, map, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { EnvironmentconfigService } from './environmentconfig.service';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private environmentconfigService: EnvironmentconfigService
  ) {}

  private getBaseUrl(): string {
    return this.environmentconfigService.authConfig.MVMBackEnd;
  }

  private buildFileApiUrl(path: string): string {
    const baseUrl = (this.getBaseUrl() ?? '').replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const apiBaseUrl = /\/api$/i.test(baseUrl) ? `${baseUrl}/api` : `${baseUrl}/api`;
    return `${apiBaseUrl}${normalizedPath}`;
  }

  private getLang(): string {
    const lang = sessionStorage.getItem('wmsmlang');
    if (!lang || lang === 'undefined') {
      return this.authService.getCurrentUserInfo()?.lang ?? 'zh-TW';
    }
    return lang;
  }

  private buildAuthHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set('Accept-Language', this.getLang())
      .set('Authorization', 'Bearer ' + this.authService.getToken())
      .set('CurrentUserID', this.authService.getCurrentUserInfo()?.userId ?? '')
      .set('IdentityType', this.authService.getCurrentUserInfo()?.userType || '')
      .set('X-Requested-From', 'UI');
  }

  private normalizeEphemeralKeyResponse(response: any): { key: string; iv: string } {
    const key = response?.key ?? response?.data?.key ?? response?.data?.Key;
    const iv = response?.iv ?? response?.data?.iv ?? response?.data?.Iv;

    if (!key || !iv) {
      throw new Error('Ephemeral key response is missing key/iv.');
    }

    return { key, iv };
  }

  // 獲取一次性密鑰
  getEphemeralKey(): Observable<{ key: string; iv: string }> {
    const url = this.buildFileApiUrl('/File/EphemeralKey');
    return this.http.get<any>(url, {
      headers: this.buildAuthHeaders(),
    }).pipe(
      map((response) => this.normalizeEphemeralKeyResponse(response))
    );
  }

  // AES-CBC 加密
  aesEncrypt(data: ArrayBuffer, keyBase64: string, ivBase64: string): Observable<ArrayBuffer> {
    const keyBytes = Uint8Array.from(atob(keyBase64), (c) => c.charCodeAt(0));
    const ivBytes = Uint8Array.from(atob(ivBase64), (c) => c.charCodeAt(0));

    return new Observable<ArrayBuffer>((observer) => {
      if (!crypto?.subtle) {
        observer.error(new Error('WebCrypto (crypto.subtle) is not available in this runtime.'));
        return;
      }

      crypto.subtle
        .importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['encrypt'])
        .then((cryptoKey) => crypto.subtle.encrypt({ name: 'AES-CBC', iv: ivBytes }, cryptoKey, data))
        .then((encryptedBuffer) => {
          observer.next(encryptedBuffer);
          observer.complete();
        })
        .catch((err) => observer.error(err));
    });
  }

  // AES-CBC 解密
  aesDecrypt(data: ArrayBuffer, keyBase64: string, ivBase64: string): Observable<ArrayBuffer> {
    const keyBytes = Uint8Array.from(atob(keyBase64), (c) => c.charCodeAt(0));
    const ivBytes = Uint8Array.from(atob(ivBase64), (c) => c.charCodeAt(0));

    return new Observable<ArrayBuffer>((observer) => {
      if (!crypto?.subtle) {
        observer.error(new Error('WebCrypto (crypto.subtle) is not available in this runtime.'));
        return;
      }

      crypto.subtle
        .importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['decrypt'])
        .then((cryptoKey) => crypto.subtle.decrypt({ name: 'AES-CBC', iv: ivBytes }, cryptoKey, data))
        .then((decryptedBuffer) => {
          observer.next(decryptedBuffer);
          observer.complete();
        })
        .catch((err) => observer.error(err));
    });
  }

  private readFileAsArrayBuffer(file: File): Observable<ArrayBuffer> {
    return new Observable<ArrayBuffer>((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(reader.result as ArrayBuffer);
        observer.complete();
      };
      reader.onerror = (err) => observer.error(err);
      reader.readAsArrayBuffer(file);
    });
  }

  private doFileUpload(url: string, formData: FormData): Observable<any> {
    return this.http.post(url, formData, {
      headers: this.buildAuthHeaders(),
    });
  }

  private doDownloadGetResponse(url: string): Observable<HttpResponse<Blob>> {
    return this.http.get(url, {
      headers: this.buildAuthHeaders(),
      responseType: 'blob',
      observe: 'response',
    });
  }

  private parseFileNameFromResponse(resp: HttpResponse<Blob>): string {
    const disposition = resp.headers.get('Content-Disposition');
    let fileName = 'downloaded_file';

    if (disposition) {
      const filenameStarMatch = disposition.match(/filename\*\=UTF-8''([^;]+)/);
      if (filenameStarMatch && filenameStarMatch[1]) {
        try {
          fileName = decodeURIComponent(filenameStarMatch[1]);
        } catch {
          fileName = filenameStarMatch[1];
        }
      } else {
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          fileName = filenameMatch[1];
        }
      }
    }

    return fileName;
  }

  // 上傳文件
  uploadFile(file: File): Observable<any> {
    return this.getEphemeralKey().pipe(
      switchMap(({ key, iv }) =>
        this.readFileAsArrayBuffer(file).pipe(
          switchMap((fileBuffer) => this.aesEncrypt(fileBuffer, key, iv)),
          map((encryptedBuffer) => {
            const formData = new FormData();
            formData.append('file', new Blob([encryptedBuffer], { type: 'application/octet-stream' }), file.name);

            const params = [
              `ephemeralKeyBase64=${encodeURIComponent(key)}`,
              `ephemeralIvBase64=${encodeURIComponent(iv)}`,
            ].join('&');

            const url = `${this.buildFileApiUrl('/File/Upload')}?${params}`;
            return { formData, url };
          }),
          switchMap(({ formData, url }) => this.doFileUpload(url, formData))
        )
      )
    );
  }

  // 下載文件（會自動觸發瀏覽器下載）
  downloadFile(fileId: string, storageProviderName?: string): void {
    this.downloadFileBlob(fileId, storageProviderName)
      .pipe(
        tap((blob: any) => {
          const fileName = blob.__fileName || 'downloaded_file';
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(link.href);
        })
      )
      .subscribe({
        error: (err) => console.error('Error downloading file:', err),
      });
  }

  downloadFileBlob(fileId: string, storageProviderName?: string): Observable<Blob> {
    return this.getEphemeralKey().pipe(
      switchMap(({ key, iv }) => {
        const paramsArr = [
          `ephemeralKeyBase64=${encodeURIComponent(key)}`,
          `ephemeralIvBase64=${encodeURIComponent(iv)}`,
        ];
        if (storageProviderName) {
          paramsArr.push(`storageProviderName=${encodeURIComponent(storageProviderName)}`);
        }

        const params = paramsArr.join('&');
        const url = `${this.buildFileApiUrl(`/File/${encodeURIComponent(fileId)}`)}?${params}`;

        return this.doDownloadGetResponse(url).pipe(
          switchMap((resp) => {
            const fileName = this.parseFileNameFromResponse(resp);
            const contentType = resp.headers.get('Content-Type') || 'application/octet-stream';

            const body = resp.body;
            if (!body) {
              throw new Error('Empty response body');
            }

            return new Observable<Blob>((observer) => {
              body
                .arrayBuffer()
                .then((encryptedData) => {
                  this.aesDecrypt(encryptedData, key, iv).subscribe({
                    next: (decryptedBuffer) => {
                      const blob = new Blob([decryptedBuffer], { type: contentType });
                      (blob as any).__fileName = fileName;
                      observer.next(blob);
                      observer.complete();
                    },
                    error: (err) => observer.error(err),
                  });
                })
                .catch((err) => observer.error(err));
            });
          })
        );
      })
    );
  }

  // 刪除文件
  deleteFile(fileId: string): Observable<any> {
    const url = this.buildFileApiUrl(`/File/${encodeURIComponent(fileId)}`);
    return this.http.delete(url, {
      headers: this.buildAuthHeaders(),
    });
  }

  // fullUrl 外网如果无法访问，则通过后端代理下载
  getFileByFullUrl(fullUrl: string, fileName: string): Observable<HttpResponse<Blob>> {
    const url = `${this.buildFileApiUrl('/File/GetByFullUrl')}?fullUrl=${encodeURIComponent(fullUrl)}&fileName=${encodeURIComponent(fileName)}`;
    return this.http.get(url, {
      headers: this.buildAuthHeaders(),
      responseType: 'blob',
      observe: 'response',
    });
  }
}
